import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useGeolocationContext } from "@/contexts/GeolocationContext";
import { GeolocationPermissionState } from "@/services/geolocationService";
import { MapPin, MapPinOff } from "lucide-react";
import { cn } from "@/lib/utils";

export const GeolocationStatus = () => {
  const { t } = useTranslation();
  const {
    isEnabled,
    permissionState,
    isSupported,
    isLoading,
    toggleGeolocation,
  } = useGeolocationContext();

  if (!isSupported) {
    return null;
  }

  if (
    permissionState === GeolocationPermissionState.DENIED ||
    (permissionState !== GeolocationPermissionState.GRANTED && !isLoading)
  ) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        <span className="text-sm text-gray-600">
          {t("contractorVisits.geolocation.checking")}
        </span>
      </div>
    );
  }

  const getStatusColor = () => {
    return isEnabled ? "text-green-600" : "text-gray-500";
  };

  const getStatusText = () => {
    return isEnabled
      ? t("contractorVisits.geolocation.enabled")
      : t("contractorVisits.geolocation.disabled");
  };

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        {isEnabled ? (
          <MapPin className={cn("h-4 w-4", getStatusColor())} />
        ) : (
          <MapPinOff className={cn("h-4 w-4", getStatusColor())} />
        )}
        <div className="flex items-center gap-2">
          <Label
            htmlFor="geolocation-switch"
            className="text-sm font-medium cursor-pointer"
          >
            {t("contractorVisits.geolocation.label")}
          </Label>
          <span className={cn("text-xs", getStatusColor())}>
            • {getStatusText()}
          </span>
        </div>
      </div>

      <Switch
        id="geolocation-switch"
        checked={isEnabled}
        onCheckedChange={toggleGeolocation}
        disabled={isLoading}
      />
    </div>
  );
};

export const GeolocationStatusContainer = () => {
  const { permissionState, isSupported, isLoading } = useGeolocationContext();

  if (!isSupported) {
    return null;
  }

  if (
    permissionState === GeolocationPermissionState.DENIED ||
    (permissionState !== GeolocationPermissionState.GRANTED && !isLoading)
  ) {
    return null;
  }

  return (
    <div className="flex items-center justify-between py-3 mb-4 border-b border-gray-200">
      <GeolocationStatus />
    </div>
  );
};
